import { LitElement, html, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { PLAN_LABELS, type CreditBalance, type SubscriptionInfo, type SubscriptionPlan } from "../services/index.ts";
import { directRestRequest } from "../services/supabase-client.ts";
import {
  TIERS,
  getModelsForTier,
  loadTierPreferences,
  saveTierPreferences,
  getModelById,
  VISION_MODELS,
  TTS_VOICES,
  TTS_CREDIT_COST,
  loadToolPreferences,
  saveToolPreferences,
  type ModelEntry,
  type ModelTier,
  type TierPreferences,
  type ToolPreferences,
} from "../models.ts";

type ThemeMode = "light" | "dark" | "system";

function getRemaining(b: CreditBalance): number {
  return b.monthlyCredits - b.monthlyUsed + b.bonusCredits;
}
function getTotal(b: CreditBalance): number {
  return b.monthlyCredits + b.bonusCredits;
}
function getPercentage(b: CreditBalance): number {
  const total = getTotal(b);
  if (total === 0) return 0;
  return Math.round((getRemaining(b) / total) * 100);
}
function getCreditLevel(b: CreditBalance): "high" | "medium" | "low" {
  const pct = getPercentage(b);
  if (pct > 40) return "high";
  if (pct > 20) return "medium";
  return "low";
}

@customElement("katanoid-settings-view")
export class KatanoidSettingsView extends LitElement {
  protected override createRenderRoot(): HTMLElement {
    return this;
  }

  @property({ type: String }) displayName = "";
  @property({ type: String }) assistantName = "Katanoid";
  @property({ type: String }) customInstructions = "";
  @property({ type: String }) email = "";
  @property({ attribute: false }) credits: CreditBalance | null = null;
  @property({ attribute: false }) subscription: SubscriptionInfo | null = null;
  @property({ type: String }) themeMode: ThemeMode = "light";
  @property({ type: String }) apiKeyHint: string | null = null;
  @property({ attribute: false }) customModels: ModelEntry[] = [];
  @property({ type: Boolean }) memoryEnabled = true;
  @state() private tierPrefs: TierPreferences = loadTierPreferences();
  @state() private toolPrefs: ToolPreferences = loadToolPreferences();

  // ── Memory management state ──
  @state() private memoryExpanded = false;
  @state() private memoryFacts: Array<{ id: string; category: string; fact_key: string; fact_value: string }> = [];
  @state() private memoryLoading = false;
  @state() private memoryDeletingId: string | null = null;

  // ── API Key management state ──
  @state() private apiKeyInput = "";
  @state() private apiKeyLoading = false;
  @state() private apiKeyError: string | null = null;
  @state() private apiKeySuccess: string | null = null;

  // ── OpenAI Key management state ──
  @property({ type: String }) openaiKeyHint: string | null = null;
  @state() private openaiKeyInput = "";
  @state() private openaiKeyLoading = false;
  @state() private openaiKeyError: string | null = null;
  @state() private openaiKeySuccess: string | null = null;

  // ── Password change state ──
  @state() private passwordFormOpen = false;
  @state() private currentPassword = "";
  @state() private newPassword = "";
  @state() private confirmPassword = "";
  @state() private passwordLoading = false;
  @state() private passwordError: string | null = null;
  @state() private passwordSuccess = false;

  // ── Assistant settings drafts (for debounced save) ──
  @state() private assistantNameDraft = "";
  @state() private customInstructionsDraft = "";
  @state() private saveTimer: ReturnType<typeof setTimeout> | null = null;
  @state() private saveStatus: "idle" | "saving" | "saved" = "idle";

  override willUpdate(changedProperties: Map<string, unknown>): void {
    // Sync props → drafts, but only when not mid-edit (no pending save timer)
    if (changedProperties.has("assistantName") && !this.saveTimer) {
      this.assistantNameDraft = this.assistantName;
    }
    if (changedProperties.has("customInstructions") && !this.saveTimer) {
      this.customInstructionsDraft = this.customInstructions || "";
    }
  }

  protected override render(): TemplateResult {
    return html`
      <div class="main-content-padded view-enter">
        <div class="view-header">
          <h1 class="view-title">Settings</h1>
        </div>

        ${this.renderAccountSection()}
        ${this.renderSecuritySection()}
        ${this.renderSubscriptionSection()}
        ${this.renderAppearanceSection()}
        ${this.renderModelsSection()}
        ${this.renderToolModelsSection()}
        ${this.renderMemorySection()}
        ${this.renderAssistantSection()}
        ${this.renderAdvancedSection()}
        ${this.renderSignOutSection()}
      </div>
    `;
  }

  private renderAccountSection(): TemplateResult {
    const initial = (this.displayName || this.email || "?").charAt(0).toUpperCase();

    return html`
      <div class="settings-section">
        <div class="settings-section-title">Account</div>
        <div class="settings-group">
          <div class="settings-row">
            <div class="settings-account">
              <div class="avatar avatar--sm">${initial}</div>
              <div>
                <div class="settings-account-name">${this.displayName || "User"}</div>
                <div class="settings-account-email">${this.email}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderSecuritySection(): TemplateResult {
    return html`
      <div class="settings-section">
        <div class="settings-section-title">Security</div>
        <div class="settings-group">
          ${this.passwordFormOpen
            ? html`
                <div class="settings-password-form">
                  ${this.passwordError
                    ? html`<div class="settings-password-error">${this.passwordError}</div>`
                    : ""}
                  ${this.passwordSuccess
                    ? html`<div class="settings-password-success">Password updated successfully</div>`
                    : ""}
                  <label class="settings-field-label">Current password</label>
                  <input
                    type="password"
                    class="settings-input"
                    placeholder="Enter current password"
                    .value=${this.currentPassword}
                    @input=${(e: InputEvent) => { this.currentPassword = (e.target as HTMLInputElement).value; }}
                    ?disabled=${this.passwordLoading}
                  />
                  <label class="settings-field-label">New password</label>
                  <input
                    type="password"
                    class="settings-input"
                    placeholder="Min 8 characters"
                    .value=${this.newPassword}
                    @input=${(e: InputEvent) => { this.newPassword = (e.target as HTMLInputElement).value; }}
                    ?disabled=${this.passwordLoading}
                  />
                  <label class="settings-field-label">Confirm new password</label>
                  <input
                    type="password"
                    class="settings-input"
                    placeholder="Re-enter new password"
                    .value=${this.confirmPassword}
                    @input=${(e: InputEvent) => { this.confirmPassword = (e.target as HTMLInputElement).value; }}
                    ?disabled=${this.passwordLoading}
                  />
                  <div class="settings-password-actions">
                    <button
                      class="btn btn--primary btn--sm"
                      @click=${this.handleChangePassword}
                      ?disabled=${this.passwordLoading}
                    >
                      ${this.passwordLoading ? html`<span class="spinner spinner--sm"></span>` : "Update password"}
                    </button>
                    <button
                      class="btn btn--ghost btn--sm"
                      @click=${this.handleCancelPasswordChange}
                      ?disabled=${this.passwordLoading}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              `
            : html`
                <div class="settings-row">
                  <span class="settings-row-label">Password</span>
                  <button class="btn btn--ghost btn--sm" @click=${() => { this.passwordFormOpen = true; this.passwordSuccess = false; this.passwordError = null; }}>
                    Change password
                  </button>
                </div>
              `}
        </div>
      </div>
    `;
  }

  private handleChangePassword = (): void => {
    this.passwordError = null;
    this.passwordSuccess = false;

    if (this.newPassword.length < 8) {
      this.passwordError = "New password must be at least 8 characters";
      return;
    }
    if (this.newPassword !== this.confirmPassword) {
      this.passwordError = "Passwords do not match";
      return;
    }
    if (!this.currentPassword) {
      this.passwordError = "Enter your current password";
      return;
    }

    this.passwordLoading = true;
    this.dispatchEvent(
      new CustomEvent("change-password", {
        detail: {
          currentPassword: this.currentPassword,
          newPassword: this.newPassword,
        },
        bubbles: true,
        composed: true,
      })
    );
  };

  private handleCancelPasswordChange = (): void => {
    this.passwordFormOpen = false;
    this.currentPassword = "";
    this.newPassword = "";
    this.confirmPassword = "";
    this.passwordError = null;
    this.passwordSuccess = false;
  };

  /** Called by consumer-app on successful password change */
  public passwordChanged(): void {
    this.passwordLoading = false;
    this.passwordSuccess = true;
    this.currentPassword = "";
    this.newPassword = "";
    this.confirmPassword = "";
    this.passwordError = null;
    // Auto-collapse after a moment
    setTimeout(() => {
      this.passwordFormOpen = false;
      this.passwordSuccess = false;
    }, 2000);
  }

  /** Called by consumer-app on failed password change */
  public passwordChangeFailed(error: string): void {
    this.passwordLoading = false;
    this.passwordError = error;
  }

  private renderSubscriptionSection(): TemplateResult {
    if (!this.credits) {
      return html`
        <div class="settings-section">
          <div class="settings-section-title">Subscription</div>
          <div class="settings-group">
            <div class="settings-row">
              <span class="settings-row-label">Loading...</span>
              <span class="spinner spinner--sm"></span>
            </div>
          </div>
        </div>
      `;
    }

    const remaining = getRemaining(this.credits);
    const total = getTotal(this.credits);
    const pct = getPercentage(this.credits);
    const level = getCreditLevel(this.credits);

    const plan = this.subscription?.plan ?? "free";
    const planLabel = PLAN_LABELS[plan] ?? "Free";
    const isFree = plan === "free";
    const isPastDue = this.subscription?.status === "past_due";
    const pendingPlan = this.subscription?.pendingPlan ?? null;
    const pendingPlanLabel = pendingPlan ? (PLAN_LABELS[pendingPlan] ?? pendingPlan) : null;

    return html`
      <div class="settings-section">
        <div class="settings-section-title">Subscription & Credits</div>
        <div class="settings-group">
          <div class="settings-row">
            <span class="settings-row-label">Plan</span>
            <span class="settings-row-value">${planLabel}</span>
          </div>
          ${isPastDue
            ? html`
                <div class="settings-row">
                  <span class="settings-plan-warning">Payment past due \u2014 please update your payment method</span>
                </div>
              `
            : ""}
          ${pendingPlan && this.subscription?.currentPeriodEnd
            ? html`
                <div class="settings-row" style="flex-direction: column; align-items: stretch; gap: var(--space-2);">
                  <div style="font-size: var(--text-sm); color: var(--text-secondary);">
                    Switching to <strong>${pendingPlanLabel}</strong> on ${new Date(this.subscription.currentPeriodEnd).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
                  </div>
                  <button
                    class="btn btn--ghost btn--sm"
                    style="align-self: flex-start; font-size: var(--text-xs);"
                    @click=${this.handleCancelPendingDowngrade}
                  >
                    Cancel plan change
                  </button>
                </div>
              `
            : !isFree && this.subscription?.currentPeriodEnd
              ? html`
                  <div class="settings-row">
                    <span class="settings-plan-renewal">
                      Renews ${new Date(this.subscription.currentPeriodEnd).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
                    </span>
                  </div>
                `
              : ""}
          <div class="settings-row">
            <div class="settings-credit-wrapper">
              <div class="credit-bar">
                <div class="credit-bar-track">
                  <div
                    class="credit-bar-fill ${level === "low" ? "credit-bar-fill--critical" : level === "medium" ? "credit-bar-fill--low" : ""}"
                    style="width: ${pct}%"
                  ></div>
                </div>
                <div class="credit-bar-label">
                  ${remaining} of ${total} credits remaining
                </div>
                ${level === "low"
                  ? html`<div class="credit-bar-warning">Running low \u2014 buy more credits</div>`
                  : ""}
              </div>
            </div>
          </div>
          <div class="settings-row settings-row--center" style="gap: var(--space-3);">
            ${isFree
              ? html`<button class="btn btn--primary btn--sm" @click=${this.handleUpgrade}>Upgrade to Pro</button>`
              : html`
                  <button class="btn btn--primary btn--sm" @click=${this.handleUpgrade}>Change Plan</button>
                  <button class="btn btn--ghost btn--sm" @click=${this.handleManageSubscription}>Manage Subscription</button>
                `
            }
            <button class="btn btn--ghost btn--sm" @click=${this.handleBuyCredits}>Buy Credits</button>
          </div>
        </div>
      </div>
    `;
  }

  private handleUpgrade(): void {
    this.dispatchEvent(
      new CustomEvent("billing-action", {
        detail: { action: "upgrade" },
        bubbles: true,
        composed: true,
      })
    );
  }

  private handleManageSubscription(): void {
    this.dispatchEvent(
      new CustomEvent("billing-action", {
        detail: { action: "manage" },
        bubbles: true,
        composed: true,
      })
    );
  }

  private handleCancelPendingDowngrade(): void {
    this.dispatchEvent(
      new CustomEvent("billing-cancel-downgrade", {
        bubbles: true,
        composed: true,
      })
    );
  }

  private handleBuyCredits(): void {
    this.dispatchEvent(
      new CustomEvent("billing-action", {
        detail: { action: "buy-credits" },
        bubbles: true,
        composed: true,
      })
    );
  }

  // ── API Key Section ──

  private renderAdvancedSection(): TemplateResult {
    const hasKey = !!this.apiKeyHint;
    const hasOpenaiKey = !!this.openaiKeyHint;
    const plan = this.subscription?.plan ?? "free";
    const isPaid = plan !== "free";

    return html`
      <div class="settings-section">
        <div class="settings-section-title">API Keys</div>
        <div class="settings-group">
          ${!isPaid
            ? html`
                <div class="settings-row" style="flex-direction: column; align-items: stretch; gap: var(--space-2);">
                  <div style="font-size: var(--text-sm); color: var(--text-secondary);">
                    Bring your own API keys to skip credit costs.
                  </div>
                  <div style="font-size: var(--text-xs); color: var(--text-tertiary);">
                    Available on paid plans.
                    <button
                      class="btn btn--ghost btn--sm"
                      style="font-size: var(--text-xs); padding: 2px 8px; display: inline;"
                      @click=${this.handleUpgrade}
                    >
                      Upgrade
                    </button>
                  </div>
                </div>
              `
            : html`
                <!-- OpenRouter Key -->
                ${hasKey
                  ? html`
                      <div class="settings-row">
                        <span class="settings-row-label">OpenRouter Key</span>
                        <div style="display: flex; align-items: center; gap: var(--space-2);">
                          <span class="settings-row-value">\u2022\u2022\u2022\u2022${this.apiKeyHint}</span>
                          <button
                            class="btn btn--ghost btn--sm"
                            @click=${this.handleDeleteApiKey}
                            ?disabled=${this.apiKeyLoading}
                            style="font-size: var(--text-xs); padding: 2px 8px;"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                      <div style="font-size: var(--text-xs); color: var(--text-tertiary); padding: 0 var(--space-1);">
                        Used for chat &amp; tools. Messages sent with your key won't consume credits.
                      </div>
                    `
                  : html`
                      <div class="settings-row" style="flex-direction: column; align-items: stretch; gap: var(--space-2);">
                        <span class="settings-row-label">OpenRouter API Key</span>
                        <div style="display: flex; gap: var(--space-2);">
                          <input
                            class="input"
                            type="password"
                            placeholder="sk-or-v1-..."
                            .value=${this.apiKeyInput}
                            style="flex: 1; height: 36px; font-size: var(--text-sm);"
                            @input=${(e: InputEvent) => {
                              this.apiKeyInput = (e.target as HTMLInputElement).value;
                              this.apiKeyError = null;
                              this.apiKeySuccess = null;
                            }}
                          />
                          <button
                            class="btn btn--primary btn--sm"
                            @click=${this.handleSaveApiKey}
                            ?disabled=${this.apiKeyLoading || !this.apiKeyInput.trim()}
                          >
                            ${this.apiKeyLoading ? "Saving\u2026" : "Save Key"}
                          </button>
                        </div>
                        ${this.apiKeyError
                          ? html`<div style="font-size: var(--text-xs); color: var(--red, #ef4444);">${this.apiKeyError}</div>`
                          : ""}
                        ${this.apiKeySuccess
                          ? html`<div style="font-size: var(--text-xs); color: var(--green, #22c55e);">${this.apiKeySuccess}</div>`
                          : ""}
                        <div style="font-size: var(--text-xs); color: var(--text-tertiary);">
                          Used for chat &amp; tools (web search, image analysis, etc). Get one at
                          <a href="https://openrouter.ai/keys" target="_blank" rel="noopener" style="color: var(--primary);">openrouter.ai/keys</a>
                        </div>
                      </div>
                    `
                }

                <!-- OpenAI Key (for TTS) -->
                <div style="border-top: 1px solid var(--border); margin-top: var(--space-1); padding-top: var(--space-2);"></div>
                ${hasOpenaiKey
                  ? html`
                      <div class="settings-row">
                        <span class="settings-row-label">OpenAI Key</span>
                        <div style="display: flex; align-items: center; gap: var(--space-2);">
                          <span class="settings-row-value">\u2022\u2022\u2022\u2022${this.openaiKeyHint}</span>
                          <button
                            class="btn btn--ghost btn--sm"
                            @click=${this.handleDeleteOpenaiKey}
                            ?disabled=${this.openaiKeyLoading}
                            style="font-size: var(--text-xs); padding: 2px 8px;"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                      <div style="font-size: var(--text-xs); color: var(--text-tertiary); padding: 0 var(--space-1);">
                        Used only for text-to-speech (TTS). TTS won't consume credits with your own key.
                      </div>
                    `
                  : html`
                      <div class="settings-row" style="flex-direction: column; align-items: stretch; gap: var(--space-2);">
                        <span class="settings-row-label">OpenAI API Key <span style="font-weight: normal; color: var(--text-tertiary);">(optional \u2014 for TTS)</span></span>
                        <div style="display: flex; gap: var(--space-2);">
                          <input
                            class="input"
                            type="password"
                            placeholder="sk-..."
                            .value=${this.openaiKeyInput}
                            style="flex: 1; height: 36px; font-size: var(--text-sm);"
                            @input=${(e: InputEvent) => {
                              this.openaiKeyInput = (e.target as HTMLInputElement).value;
                              this.openaiKeyError = null;
                              this.openaiKeySuccess = null;
                            }}
                          />
                          <button
                            class="btn btn--primary btn--sm"
                            @click=${this.handleSaveOpenaiKey}
                            ?disabled=${this.openaiKeyLoading || !this.openaiKeyInput.trim()}
                          >
                            ${this.openaiKeyLoading ? "Saving\u2026" : "Save Key"}
                          </button>
                        </div>
                        ${this.openaiKeyError
                          ? html`<div style="font-size: var(--text-xs); color: var(--red, #ef4444);">${this.openaiKeyError}</div>`
                          : ""}
                        ${this.openaiKeySuccess
                          ? html`<div style="font-size: var(--text-xs); color: var(--green, #22c55e);">${this.openaiKeySuccess}</div>`
                          : ""}
                        <div style="font-size: var(--text-xs); color: var(--text-tertiary);">
                          Used only for text-to-speech (TTS). Saves TTS credit costs. Get one at
                          <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener" style="color: var(--primary);">platform.openai.com/api-keys</a>
                        </div>
                      </div>
                    `
                }
              `
          }
        </div>
      </div>
    `;
  }

  private handleRemoveCustomModel(modelId: string): void {
    this.dispatchEvent(
      new CustomEvent("model-remove", {
        detail: { modelId },
        bubbles: true,
        composed: true,
      })
    );
  }

  private async handleSaveApiKey(): Promise<void> {
    const key = this.apiKeyInput.trim();
    if (!key) return;

    this.apiKeyLoading = true;
    this.apiKeyError = null;
    this.apiKeySuccess = null;

    try {
      // Dispatch event — consumer-app handles the actual service call
      this.dispatchEvent(
        new CustomEvent("api-key-save", {
          detail: { key },
          bubbles: true,
          composed: true,
        })
      );
    } catch {
      this.apiKeyError = "Failed to save API key.";
      this.apiKeyLoading = false;
    }
  }

  private handleDeleteApiKey(): void {
    this.apiKeyLoading = true;
    this.dispatchEvent(
      new CustomEvent("api-key-delete", {
        bubbles: true,
        composed: true,
      })
    );
  }

  private handleBrowseModels(): void {
    this.dispatchEvent(
      new CustomEvent("browse-models", {
        bubbles: true,
        composed: true,
      })
    );
  }

  /** Called by consumer-app after save/delete completes to update local state. */
  public apiKeySaved(hint: string): void {
    this.apiKeyLoading = false;
    this.apiKeyInput = "";
    this.apiKeySuccess = "Key saved successfully.";
    this.apiKeyHint = hint;
    setTimeout(() => { this.apiKeySuccess = null; }, 3000);
  }

  public apiKeyDeleted(): void {
    this.apiKeyLoading = false;
    this.apiKeyHint = null;
  }

  public apiKeyFailed(error: string): void {
    this.apiKeyLoading = false;
    this.apiKeyError = error;
  }

  // ── OpenAI Key Handlers ──

  private async handleSaveOpenaiKey(): Promise<void> {
    const key = this.openaiKeyInput.trim();
    if (!key) return;

    this.openaiKeyLoading = true;
    this.openaiKeyError = null;
    this.openaiKeySuccess = null;

    try {
      this.dispatchEvent(
        new CustomEvent("openai-key-save", {
          detail: { key },
          bubbles: true,
          composed: true,
        })
      );
    } catch {
      this.openaiKeyError = "Failed to save OpenAI key.";
      this.openaiKeyLoading = false;
    }
  }

  private handleDeleteOpenaiKey(): void {
    this.openaiKeyLoading = true;
    this.dispatchEvent(
      new CustomEvent("openai-key-delete", {
        bubbles: true,
        composed: true,
      })
    );
  }

  public openaiKeySaved(hint: string): void {
    this.openaiKeyLoading = false;
    this.openaiKeyInput = "";
    this.openaiKeySuccess = "OpenAI key saved successfully.";
    this.openaiKeyHint = hint;
    setTimeout(() => { this.openaiKeySuccess = null; }, 3000);
  }

  public openaiKeyDeleted(): void {
    this.openaiKeyLoading = false;
    this.openaiKeyHint = null;
  }

  public openaiKeyFailed(error: string): void {
    this.openaiKeyLoading = false;
    this.openaiKeyError = error;
  }

  private renderAppearanceSection(): TemplateResult {
    return html`
      <div class="settings-section">
        <div class="settings-section-title">Appearance</div>
        <div class="settings-group">
          <div class="settings-row">
            <span class="settings-row-label">Theme</span>
            <div class="theme-toggle">
              <button
                class="theme-toggle-btn ${this.themeMode === "light" ? "theme-toggle-btn--active" : ""}"
                @click=${() => this.setTheme("light")}
              >
                Light
              </button>
              <button
                class="theme-toggle-btn ${this.themeMode === "dark" ? "theme-toggle-btn--active" : ""}"
                @click=${() => this.setTheme("dark")}
              >
                Dark
              </button>
              <button
                class="theme-toggle-btn ${this.themeMode === "system" ? "theme-toggle-btn--active" : ""}"
                @click=${() => this.setTheme("system")}
              >
                System
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderModelsSection(): TemplateResult {
    const customModels = this.customModels;

    return html`
      <div class="settings-section">
        <div class="settings-section-title">Models</div>
        <div class="settings-group">
          ${TIERS.map(
            (tier) => html`
              <div class="settings-row">
                <span class="settings-row-label">
                  ${tier.label}
                  <span class="settings-row-hint">${tier.mult}</span>
                </span>
                <select
                  class="settings-select"
                  .value=${this.tierPrefs[tier.id]}
                  @change=${(e: Event) =>
                    this.handleModelChange(tier.id, (e.target as HTMLSelectElement).value)}
                >
                  ${getModelsForTier(tier.id).map(
                    (model) => html`
                      <option
                        value=${model.id}
                        ?selected=${model.id === this.tierPrefs[tier.id]}
                      >
                        ${model.label}${model.provider ? ` (${model.provider})` : ""}
                      </option>
                    `
                  )}
                </select>
              </div>
            `
          )}

          ${customModels.map(
            (m) => {
              const tierLabel = TIERS.find((t) => t.id === m.tier)?.label ?? m.tier;
              return html`
                <div class="settings-row">
                  <span class="settings-row-label">
                    ${m.label}
                    <span class="settings-row-hint">${tierLabel}</span>
                  </span>
                  <button class="btn btn--ghost btn--sm" style="padding: var(--space-1) var(--space-2); height: auto; min-height: 0;" @click=${() => this.handleRemoveCustomModel(m.id)}>Remove</button>
                </div>
              `;
            }
          )}

          <div class="settings-row" style="justify-content: center;">
            <button class="btn btn--ghost btn--sm" @click=${this.handleBrowseModels}>Browse more models</button>
          </div>
        </div>
      </div>
    `;
  }

  private handleModelChange(tier: ModelTier, modelId: string): void {
    // Validate the model exists
    if (!getModelById(modelId)) return;
    this.tierPrefs = { ...this.tierPrefs, [tier]: modelId };
    saveTierPreferences(this.tierPrefs);
    this.dispatchEvent(
      new CustomEvent("model-prefs-change", {
        detail: { tier, modelId, prefs: this.tierPrefs },
        bubbles: true,
        composed: true,
      })
    );
  }

  private renderToolModelsSection(): TemplateResult {
    const selectedVision = VISION_MODELS.find((m) => m.id === this.toolPrefs.visionModel);
    const isByok = this.subscription?.plan === "byok" || !!this.apiKeyHint;
    const hasOpenaiKey = !!this.openaiKeyHint;
    // TTS is only free when user has their own OpenAI key (since TTS calls OpenAI directly)
    const ttsFree = hasOpenaiKey;

    return html`
      <div class="settings-section">
        <div class="settings-section-title">Tool Models</div>
        <div class="settings-group">
          <div style="font-size: var(--text-xs); color: var(--text-tertiary); padding: 0 var(--space-1); margin-bottom: var(--space-1);">
            ${isByok
              ? hasOpenaiKey
                ? "Tools use your API keys \u2014 no additional credits charged."
                : "Most tools use your OpenRouter key (free). TTS uses OpenAI \u2014 add an OpenAI key to avoid TTS credits."
              : "Credits shown are charged in addition to the base message cost when a tool is used."}
          </div>
          <div class="settings-row">
            <span class="settings-row-label">
              Image Analysis
              <span class="settings-row-hint">${isByok
                ? "free with your key"
                : selectedVision
                  ? `+${selectedVision.creditCost} credit${selectedVision.creditCost !== 1 ? "s" : ""}`
                  : ""}</span>
            </span>
            <select
              class="settings-select"
              .value=${this.toolPrefs.visionModel}
              @change=${(e: Event) => this.handleToolPrefChange("visionModel", (e.target as HTMLSelectElement).value)}
            >
              ${VISION_MODELS.map(
                (m) => html`
                  <option value=${m.id} ?selected=${m.id === this.toolPrefs.visionModel}>
                    ${m.label}${isByok ? "" : ` \u2014 ${m.creditCost === 0 ? "free" : `+${m.creditCost} credit${m.creditCost !== 1 ? "s" : ""}`}`}
                  </option>
                `
              )}
            </select>
          </div>
          <div class="settings-row">
            <span class="settings-row-label">
              Text-to-Speech Voice
              <span class="settings-row-hint">${ttsFree ? "free with your key" : `+${TTS_CREDIT_COST} credit/use`}</span>
            </span>
            <select
              class="settings-select"
              .value=${this.toolPrefs.ttsVoice}
              @change=${(e: Event) => this.handleToolPrefChange("ttsVoice", (e.target as HTMLSelectElement).value)}
            >
              ${TTS_VOICES.map(
                (v) => html`
                  <option value=${v.id} ?selected=${v.id === this.toolPrefs.ttsVoice}>
                    ${v.label} \u2014 ${v.description}
                  </option>
                `
              )}
            </select>
          </div>
          <div class="settings-row">
            <span class="settings-row-label">
              Voice Input Mode
              <span class="settings-row-hint">how the mic button works</span>
            </span>
            <select
              class="settings-select"
              .value=${this.toolPrefs.voiceMode}
              @change=${(e: Event) => this.handleToolPrefChange("voiceMode", (e.target as HTMLSelectElement).value)}
            >
              <option value="toggle" ?selected=${this.toolPrefs.voiceMode === "toggle"}>
                Toggle \u2014 Click to start/stop
              </option>
              <option value="hold" ?selected=${this.toolPrefs.voiceMode === "hold"}>
                Hold to talk \u2014 Hold button while speaking
              </option>
            </select>
          </div>
        </div>
      </div>
    `;
  }

  private renderMemorySection(): TemplateResult {
    const plan = this.subscription?.plan ?? "free";
    const isFree = plan === "free";

    if (isFree) {
      return html`
        <div class="settings-section">
          <div class="settings-section-title">Memory</div>
          <div class="settings-group">
            <div class="settings-row" style="flex-direction: column; align-items: stretch; gap: var(--space-2);">
              <div style="font-size: var(--text-sm); color: var(--text-secondary);">
                Memory allows the assistant to remember information about you across conversations.
              </div>
              <div style="font-size: var(--text-xs); color: var(--text-tertiary);">
                Available on paid plans.
                <button
                  class="btn btn--ghost btn--sm"
                  style="font-size: var(--text-xs); padding: 2px 8px; display: inline;"
                  @click=${this.handleUpgrade}
                >
                  Upgrade
                </button>
              </div>
            </div>
          </div>
        </div>
      `;
    }

    // Group facts by category for display
    const grouped: Record<string, typeof this.memoryFacts> = {};
    for (const fact of this.memoryFacts) {
      if (!grouped[fact.category]) grouped[fact.category] = [];
      grouped[fact.category].push(fact);
    }
    const categoryLabels: Record<string, string> = {
      personal: "Personal",
      preference: "Preferences",
      work: "Work",
      technical: "Technical",
      relationship: "Relationships",
      other: "Other",
    };

    return html`
      <div class="settings-section">
        <div class="settings-section-title">Memory</div>
        <div class="settings-group">
          <div class="settings-row">
            <div style="flex: 1;">
              <span class="settings-row-label">Remember conversations</span>
              <div style="font-size: var(--text-xs); color: var(--text-tertiary); margin-top: 2px;">
                The assistant learns about you from conversations and personalizes future responses.
              </div>
            </div>
            <label class="skill-toggle-switch" style="flex-shrink: 0;">
              <input
                type="checkbox"
                .checked=${this.memoryEnabled}
                @change=${(e: Event) => {
                  const enabled = (e.target as HTMLInputElement).checked;
                  this.memoryEnabled = enabled;
                  this.dispatchEvent(
                    new CustomEvent("memory-toggle", {
                      detail: { enabled },
                      bubbles: true,
                      composed: true,
                    })
                  );
                }}
              />
              <span class="skill-toggle-switch__track"></span>
            </label>
          </div>

          ${this.memoryEnabled
            ? html`
                <div class="settings-row" style="justify-content: center;">
                  <button
                    class="btn btn--ghost btn--sm"
                    @click=${this.handleToggleMemoryView}
                    ?disabled=${this.memoryLoading}
                  >
                    ${this.memoryLoading
                      ? "Loading\u2026"
                      : this.memoryExpanded
                        ? "Hide Memory"
                        : `Manage Memory${this.memoryFacts.length > 0 ? ` (${this.memoryFacts.length})` : ""}`}
                  </button>
                </div>
              `
            : ""}

          ${this.memoryExpanded && this.memoryEnabled
            ? html`
                ${this.memoryFacts.length === 0
                  ? html`
                      <div style="padding: var(--space-3); text-align: center; font-size: var(--text-sm); color: var(--text-tertiary);">
                        No memories yet. The assistant will start remembering as you chat.
                      </div>
                    `
                  : html`
                      <div style="display: flex; flex-direction: column; gap: var(--space-3); padding: var(--space-2) 0;">
                        ${Object.entries(grouped).map(
                          ([category, facts]) => html`
                            <div>
                              <div style="font-size: var(--text-xs); font-weight: 600; color: var(--text-secondary); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: var(--space-1); padding: 0 var(--space-1);">
                                ${categoryLabels[category] ?? category}
                              </div>
                              <div style="display: flex; flex-direction: column; gap: 2px;">
                                ${facts.map(
                                  (fact) => html`
                                    <div
                                      class="settings-row"
                                      style="padding: var(--space-1) var(--space-1); min-height: 0; gap: var(--space-2); align-items: flex-start;"
                                    >
                                      <div style="flex: 1; min-width: 0;">
                                        <div style="font-size: var(--text-sm); color: var(--text-primary); word-break: break-word;">
                                          <span style="color: var(--text-tertiary);">${fact.fact_key}:</span>
                                          ${fact.fact_value}
                                        </div>
                                      </div>
                                      <button
                                        class="btn btn--ghost btn--sm"
                                        style="padding: 2px 6px; height: auto; min-height: 0; font-size: var(--text-xs); flex-shrink: 0; color: var(--text-tertiary);"
                                        @click=${() => this.handleDeleteFact(fact.id)}
                                        ?disabled=${this.memoryDeletingId === fact.id}
                                        title="Delete this memory"
                                      >
                                        ${this.memoryDeletingId === fact.id ? "\u2026" : "\u2715"}
                                      </button>
                                    </div>
                                  `
                                )}
                              </div>
                            </div>
                          `
                        )}
                      </div>
                      <div class="settings-row" style="justify-content: center; border-top: 1px solid var(--border); padding-top: var(--space-2);">
                        <button
                          class="btn btn--ghost btn--sm"
                          style="color: var(--red, #ef4444); font-size: var(--text-xs);"
                          @click=${this.handleClearAllMemory}
                        >
                          Clear All Memory
                        </button>
                      </div>
                    `}
              `
            : ""}
        </div>
      </div>
    `;
  }

  private async handleToggleMemoryView(): Promise<void> {
    if (this.memoryExpanded) {
      this.memoryExpanded = false;
      return;
    }

    // Load facts when expanding
    this.memoryLoading = true;
    try {
      const resp = await directRestRequest(
        `/memory_facts?select=id,category,fact_key,fact_value&order=category.asc,updated_at.desc`
      );
      if (resp.ok) {
        this.memoryFacts = await resp.json();
      }
    } catch (err) {
      console.error("[Settings] Failed to load memory facts:", err);
    }
    this.memoryLoading = false;
    this.memoryExpanded = true;
  }

  private async handleDeleteFact(factId: string): Promise<void> {
    this.memoryDeletingId = factId;
    try {
      const resp = await directRestRequest(`/memory_facts?id=eq.${factId}`, {
        method: "DELETE",
      });
      if (resp.ok) {
        this.memoryFacts = this.memoryFacts.filter((f) => f.id !== factId);
      }
    } catch (err) {
      console.error("[Settings] Failed to delete fact:", err);
    }
    this.memoryDeletingId = null;
  }

  private async handleClearAllMemory(): Promise<void> {
    if (!confirm("Are you sure you want to clear all memory? This cannot be undone.")) {
      return;
    }
    this.memoryLoading = true;
    try {
      // Delete all facts — RLS ensures only own rows are deleted.
      // We need to match all rows for this user, use a filter that always matches.
      const resp = await directRestRequest(`/memory_facts?id=not.is.null`, {
        method: "DELETE",
      });
      if (resp.ok) {
        this.memoryFacts = [];
      }
    } catch (err) {
      console.error("[Settings] Failed to clear memory:", err);
    }
    this.memoryLoading = false;
  }

  private handleToolPrefChange(key: keyof ToolPreferences, value: string): void {
    this.toolPrefs = { ...this.toolPrefs, [key]: value };
    saveToolPreferences(this.toolPrefs);
    this.dispatchEvent(
      new CustomEvent("tool-prefs-change", {
        detail: { prefs: this.toolPrefs },
        bubbles: true,
        composed: true,
      })
    );
  }

  private renderAssistantSection(): TemplateResult {
    const charCount = this.customInstructionsDraft?.length ?? 0;
    const maxChars = 2000;

    return html`
      <div class="settings-section">
        <div class="settings-section-title">Assistant</div>
        <div class="settings-group">
          <div class="settings-row">
            <span class="settings-row-label">Name</span>
            <input
              class="input"
              type="text"
              .value=${this.assistantNameDraft}
              placeholder="Katanoid"
              maxlength="50"
              style="max-width: 200px; height: 36px; font-size: var(--text-sm);"
              @input=${(e: InputEvent) => {
                this.assistantNameDraft = (e.target as HTMLInputElement).value;
                this.debounceSave();
              }}
            />
          </div>
          <div class="settings-row" style="flex-direction: column; align-items: stretch; gap: var(--space-2);">
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <span class="settings-row-label">Custom Instructions</span>
              ${this.saveStatus === "saving"
                ? html`<span style="font-size: var(--text-xs); color: var(--text-tertiary);">Saving\u2026</span>`
                : this.saveStatus === "saved"
                  ? html`<span style="font-size: var(--text-xs); color: var(--green, #22c55e);">Saved</span>`
                  : ""}
            </div>
            <textarea
              class="input"
              placeholder="e.g. Always respond in Spanish. Use a casual tone. You are an expert in TypeScript and React."
              .value=${this.customInstructionsDraft}
              maxlength="${maxChars}"
              rows="4"
              style="min-height: 80px; resize: vertical; line-height: 1.5; height: auto; font-size: var(--text-sm);"
              @input=${(e: InputEvent) => {
                this.customInstructionsDraft = (e.target as HTMLTextAreaElement).value;
                this.debounceSave();
              }}
            ></textarea>
            <div style="display: flex; justify-content: space-between; font-size: var(--text-xs); color: var(--text-tertiary);">
              <span>These instructions will be included in every conversation.</span>
              <span>${charCount} / ${maxChars}</span>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private debounceSave(): void {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveStatus = "idle";
    this.saveTimer = setTimeout(() => {
      this.saveTimer = null;
      this.persistAssistantSettings();
    }, 800);
  }

  private persistAssistantSettings(): void {
    this.saveStatus = "saving";
    this.dispatchEvent(
      new CustomEvent("assistant-settings-change", {
        detail: {
          assistantName: this.assistantNameDraft.trim() || "Katanoid",
          customInstructions: this.customInstructionsDraft.trim() || null,
        },
        bubbles: true,
        composed: true,
      })
    );
    setTimeout(() => {
      this.saveStatus = "saved";
      setTimeout(() => { this.saveStatus = "idle"; }, 2000);
    }, 300);
  }

  private renderSignOutSection(): TemplateResult {
    return html`
      <div class="settings-signout">
        <button
          class="btn btn--danger btn--full"
          @click=${() => {
            this.dispatchEvent(
              new CustomEvent("sign-out", { bubbles: true, composed: true })
            );
          }}
        >
          Sign Out
        </button>
      </div>
    `;
  }

  private setTheme(mode: ThemeMode): void {
    this.dispatchEvent(
      new CustomEvent("theme-change", {
        detail: { theme: mode },
        bubbles: true,
        composed: true,
      })
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "katanoid-settings-view": KatanoidSettingsView;
  }
}
